import { useEffect, useState, useMemo } from 'react';

// Patriotic colors - Red, White, Blue
const COLORS = [
  '#FF0000', // Red
  '#FFFFFF', // White  
  '#0044FF', // Blue
];

// Generate pixel blocks for a firework burst
const generatePixelBurst = (seed, numRays = 12) => {
  const seededRandom = (s) => {
    const x = Math.sin(s * 9999) * 10000;
    return x - Math.floor(x);
  };
  
  const color = COLORS[Math.floor(seededRandom(seed) * COLORS.length)];
  
  return {
    color,
    rays: Array.from({ length: numRays }, (_, i) => {
      const angle = (i / numRays) * 360;
      const numBlocks = 4 + Math.floor(seededRandom(seed + i + 1) * 4); // 4-7 blocks per ray
      
      return {
        angle,
        blocks: Array.from({ length: numBlocks }, (_, j) => ({
          distance: 8 + j * 12, // Stepped distance
          size: j === numBlocks - 1 ? 4 : 6, // Smaller tip
          delay: j * 50,
        })),
      };
    }),
  };
};

// Single pixel block
const PixelBlock = ({ x, y, size, color, delay }) => (
  <div
    className="absolute animate-pixel-pop"
    style={{
      left: x,
      top: y,
      width: size,
      height: size,
      backgroundColor: color,
      transform: 'translate(-50%, -50%)',
      animationDelay: `${delay}ms`,
      imageRendering: 'pixelated',
    }}
  />
);

// Single firework explosion with pixel blocks
const PixelExplosion = ({ x, y, delay, seed, size = 1 }) => {
  const burst = useMemo(() => generatePixelBurst(seed), [seed]);
  
  const blocks = [];
  
  burst.rays.forEach((ray, rayIndex) => {
    const radians = (ray.angle - 90) * (Math.PI / 180);
    
    ray.blocks.forEach((block, blockIndex) => {
      const distance = block.distance * size;
      const px = Math.cos(radians) * distance;
      const py = Math.sin(radians) * distance;
      
      blocks.push(
        <PixelBlock
          key={`${rayIndex}-${blockIndex}`}
          x={`calc(50% + ${px}px)`}
          y={`calc(50% + ${py}px)`}
          size={block.size * size}
          color={burst.color}
          delay={delay + block.delay + rayIndex * 20}
        />
      );
    });
  });
  
  // Center flash
  blocks.push(
    <div
      key="center"
      className="absolute animate-pixel-flash"
      style={{
        left: '50%',
        top: '50%',
        width: 12 * size,
        height: 12 * size,
        backgroundColor: '#FFFFFF',
        transform: 'translate(-50%, -50%)',
        animationDelay: `${delay}ms`,
      }}
    />
  );
  
  return (
    <div
      className="absolute"
      style={{
        left: x,
        top: y,
        width: 160 * size,
        height: 160 * size,
        transform: 'translate(-50%, -50%)',
      }}
    >
      {blocks}
    </div>
  );
};

// Pre-defined explosion positions
const INITIAL_EXPLOSIONS = [
  { id: 1, x: '15%', y: '20%', delay: 0, seed: 1, size: 1.2 },
  { id: 2, x: '85%', y: '25%', delay: 200, seed: 2, size: 1 },
  { id: 3, x: '50%', y: '15%', delay: 400, seed: 3, size: 1.4 },
  { id: 4, x: '25%', y: '40%', delay: 600, seed: 4, size: 0.9 },
  { id: 5, x: '75%', y: '35%', delay: 800, seed: 5, size: 1.1 },
  { id: 6, x: '10%', y: '55%', delay: 1000, seed: 6, size: 0.8 },
  { id: 7, x: '90%', y: '50%', delay: 1200, seed: 7, size: 1 },
];

const EXTRA_EXPLOSIONS = [
  { x: '30%', y: '25%', size: 1 },
  { x: '70%', y: '20%', size: 0.9 },
  { x: '45%', y: '45%', size: 1.1 },
  { x: '55%', y: '30%', size: 0.8 },
  { x: '20%', y: '60%', size: 1 },
  { x: '80%', y: '55%', size: 0.9 },
];

export default function Fireworks() {
  const [extraExplosions, setExtraExplosions] = useState([]);
  const [counter, setCounter] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCounter(prev => {
        const newCounter = prev + 1;
        if (newCounter <= EXTRA_EXPLOSIONS.length) {
          const pos = EXTRA_EXPLOSIONS[newCounter - 1];
          setExtraExplosions(current => [
            ...current,
            {
              id: 100 + newCounter,
              x: pos.x,
              y: pos.y,
              delay: 0,
              seed: 100 + newCounter,
              size: pos.size,
            },
          ]);
        }
        return newCounter;
      });
    }, 500);
    
    const cleanup = setTimeout(() => {
      clearInterval(interval);
    }, 4000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(cleanup);
    };
  }, []);
  
  const allExplosions = [...INITIAL_EXPLOSIONS, ...extraExplosions];
  
  return (
    <div 
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 1 }}
    >
      {allExplosions.map((explosion) => (
        <PixelExplosion
          key={explosion.id}
          x={explosion.x}
          y={explosion.y}
          delay={explosion.delay}
          seed={explosion.seed}
          size={explosion.size}
        />
      ))}
    </div>
  );
}
